import type { PublicUser } from '@sirocco/shared';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { messages, users as usersApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/Bits';
import { Button } from '../../components/ui/Button';
import { CheckMark, SearchField } from '../../components/ui/Field';
import { useAsync, useDebounced } from '../../hooks/useAsync';
import { seedScreenCache } from '../../hooks/useAsync';
import { t } from '../../i18n';

/**
 * Ajouter des membres a un groupe.
 *
 * Les amis sont proposes d'emblee, mais la recherche porte sur tout
 * l'annuaire : on n'a pas a etre ami avec quelqu'un pour l'inviter dans
 * une discussion collective. Les membres deja dans le groupe restent
 * affiches, grises, plutot que de disparaitre — sinon on les cherche.
 */
export function AddMembersScreen() {
  const { conversationId = '' } = useParams();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);
  const [selected, setSelected] = useState<string[]>([]);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const { data, loading, error, reload } = useAsync(
    () =>
      Promise.all([
        messages.conversation(conversationId),
        /*
          Seulement des amis.

          La recherche portait sur tous les membres du spot : on pouvait
          jeter un inconnu dans un groupe, ce qui n'est un service pour
          personne. La liste part donc des amis, et la recherche ne fait
          que la filtrer — sans jamais aller chercher plus loin.
        */
        usersApi.friendList(),
      ]).then(([conversation, candidates]) => ({ conversation, candidates })),
    [conversationId, debounced],
  );

  const present = new Set(data?.conversation.members.map((member) => member.id) ?? []);
  const searching = debounced.trim().length > 0;
  /* Le filtre se fait ici : la liste des amis est deja en main, et une
     recherche qui repasserait par la base clignoterait a chaque lettre. */
  const retenus = (data?.candidates ?? []).filter((candidat) =>
    candidat.username.toLowerCase().includes(debounced.trim().toLowerCase()),
  );

  function toggle(userId: string) {
    setSelected((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId],
    );
  }

  async function add() {
    setPending(true);
    setFailure(null);
    try {
      const groupe = await messages.addMembers(conversationId, selected);
      /*
        La fiche du groupe recoit sa reponse d'avance.

        On y revenait par un `replace`, ce qui la remontait avec sa liste a
        jour — mais laissait deux etapes identiques dans l'historique : le
        balayage de retour ramenait sur elle-meme avant de mener quelque
        part. On revient donc en arriere pour de bon, et le nouveau membre
        est pose dans le cache de la fiche pour qu'elle l'affiche sans
        attendre le reseau. Meme geste que le post publie avant de revenir
        au fil.
      */
      seedScreenCache(`group:${conversationId}`, groupe);
      navigate(-1);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.messages.ajouterMembres.impossible);
    } finally {
      setPending(false);
    }
  }

  return (
    <SubScreen
      title={t.messages.ajouterMembres.titre}
      bottomBar={
        <Button block loading={pending} disabled={selected.length === 0} onClick={() => void add()}>
          {selected.length === 0
            ? t.messages.ajouterMembres.choisis
            : t.messages.ajouterMembres.ajouter(selected.length)}
        </Button>
      }
    >
      <SearchField
        tone="spot"
        className="ms-search"
        placeholder={t.messages.commun.rechercherPseudo}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      <h2 className="ms-section">{searching ? t.messages.ajouterMembres.resultats : t.messages.ajouterMembres.tesAmis}</h2>

      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}
      {failure && <p className="field__error">{failure}</p>}

      {data && retenus.length === 0 && (
        <EmptyState
          title={searching ? t.messages.commun.aucunMembre : t.messages.commun.aucunAmi}
          hint={
            searching
              ? t.messages.commun.verifie
              : t.messages.commun.chercheUnPseudo
          }
        />
      )}

      <div className="ms-contacts">
        {retenus.map((candidate) => (
          <MemberRow
            key={candidate.id}
            member={candidate}
            already={present.has(candidate.id)}
            checked={selected.includes(candidate.id)}
            onToggle={() => toggle(candidate.id)}
          />
        ))}
      </div>
    </SubScreen>
  );
}

function MemberRow({
  member,
  already,
  checked,
  onToggle,
}: {
  member: PublicUser;
  already: boolean;
  checked: boolean;
  onToggle: () => void;
}) {
  /* Deja membre : la ligne n'est plus une commande, seulement un rappel. */
  if (already) {
    return (
      <div className="ms-contact ms-contact--in">
        <Avatar user={member} size={38} />
        <span className="ms-contact__name">{member.username}</span>
        <span className="ms-contact__tag">{t.messages.ajouterMembres.dejaMembre}</span>
      </div>
    );
  }

  /* Ailleurs, c'est la ligne entiere qui coche, pas la case au bout :
     viser vingt-six pixels a chaque nom n'est pas un geste qu'on fait au
     pouce sur une longue liste. */
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      className="ms-contact ms-contact--pick"
      onClick={onToggle}
    >
      <Avatar user={member} size={38} />
      <span className="ms-contact__name">{member.username}</span>
      <CheckMark checked={checked} />
    </button>
  );
}
